import { useRef, useEffect, useState } from 'react';

const steps = [
  {
    num: '01',
    title: 'Consultation & Measurement',
    desc: 'We assess your space, discuss your style preferences and functional needs, and take precise measurements to ensure accuracy.',
    icon: 'ri-ruler-2-line',
  },
  {
    num: '02',
    title: 'Material Selection',
    desc: 'Our specialists guide you through premium flooring options — choosing the right material, color, and finish for your space.',
    icon: 'ri-palette-line',
  },
  {
    num: '03',
    title: 'Scheduling & Prep',
    desc: 'We coordinate a convenient installation date and handle all subfloor preparation to ensure a perfect base.',
    icon: 'ri-calendar-check-line',
  },
  {
    num: '04',
    title: 'Precision Installation',
    desc: 'Our skilled team installs your flooring with expert technique, attention to detail, and respect for your home.',
    icon: 'ri-tools-line',
  },
  {
    num: '05',
    title: 'Final Walkthrough',
    desc: 'We complete a thorough quality inspection with you — ensuring every edge, seam, and transition exceeds your expectations.',
    icon: 'ri-checkbox-circle-line',
  },
];

export default function ProcessSection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-charcoal relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, #C9A961 0%, transparent 50%), radial-gradient(circle at 80% 20%, #C9A961 0%, transparent 40%)',
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          className="text-center max-w-2xl mx-auto mb-20"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="section-tag mb-4 block">How We Work</span>
          <h2 className="font-serif text-4xl lg:text-5xl text-white leading-tight mb-6">
            Our Proven Installation Process
          </h2>
          <p className="text-white/50 text-base leading-relaxed">
            Every project follows the same disciplined approach — from first measurement to final walkthrough.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.num}
              className="relative"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.6s ease ${0.1 + i * 0.1}s, transform 0.6s ease ${0.1 + i * 0.1}s`,
              }}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-6 left-full w-full h-px bg-white/10 z-0 -translate-x-3" />
              )}
              <div className="relative z-10 bg-white/[0.04] border border-white/10 rounded-lg p-6 hover:border-gold/30 transition-colors duration-300 h-full">
                <div className="flex items-center justify-between mb-5">
                  <span className="font-serif text-3xl text-gold/30 font-bold">{step.num}</span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/10">
                    <i className={`${step.icon} text-gold text-base`}></i>
                  </span>
                </div>
                <h3 className="font-serif text-white text-base leading-snug mb-3">{step.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
